import React, { useState, useEffect, useRef } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu as MenuIcon, X, MapPin, Map as MapIcon, Heart, User, LogOut, Upload, Settings, Home, Video, Sparkles, Target, Calendar, Store, MessageCircle, Plus } from 'lucide-react';
import { isSequenceSectionPath } from '@/routing/sequencePaths';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import AuthPopover from '@/components/auth/AuthPopover';
import AuthDialog from '@/components/auth/AuthDialog';
import { useToast } from '@/hooks/use-toast';
import { EnhancedSearchPopup } from '@/components/search/EnhancedSearchPopup';
import SequencesMegaMenu from './SequencesMegaMenu';
import { Logo } from '@/components/ui/Logo';
import { NotificationBell } from '@/components/realtime/NotificationBell';
import { buildDisplayMapPath } from '@/routing/discoveryPaths';
import { buildAuthHref } from '@/routing/authReturnTo';
import { useAdminCapability } from '@/hooks/useAdminCapability';
import { useDisplay } from '@/hooks/useDisplays';
import { useIsSeller } from '@/hooks/useIsSeller';
import { useOwnerFocus } from '@/hooks/useOwnerFocus';

const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(true); // Always start with true
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuWasOpen = useRef(false);
  const { logout, isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { isAllowed: isAdmin } = useAdminCapability();
  // Owners who only run a show get no upload or selling prompts. The routes still work by URL.
  const showOnly = useOwnerFocus().focus === 'show';
  // Sellers keep Sales & payouts even when they say they only run a show; unknown counts as a seller.
  const isSeller = useIsSeller(showOnly);
  const hideSelling = showOnly && isSeller === false;
  const createAction = isSequenceSectionPath(location.pathname) && !showOnly
    ? { to: '/upload-sequence', label: 'Upload sequence', Icon: Upload }
    : { to: '/display/edit', label: 'Add a display', Icon: Plus };
  // Same query the display page runs, so this adds no request there. An owner already on
  // their own display page does not need a prompt to add one.
  const displayParam = matchPath('/display/:idOrSlug', location.pathname)?.params.idOrSlug;
  const { data: shownDisplay } = useDisplay(displayParam && displayParam !== 'edit' ? displayParam : undefined);
  const ownsShownDisplay = Boolean(user?.id) && (shownDisplay as { user_id?: string | null } | null | undefined)?.user_id === user?.id;
  const showCreateAction = !ownsShownDisplay;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(true); // Always keep it true
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [navigate]);

  useEffect(() => {
    if (!isMobile || !isMobileMenuOpen) {
      if (mobileMenuWasOpen.current) {
        mobileMenuWasOpen.current = false;
        mobileMenuTriggerRef.current?.focus();
      }
      return;
    }

    mobileMenuWasOpen.current = true;
    const focusTimer = window.setTimeout(() => {
      mobileMenuRef.current?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setIsMobileMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobile || !isMobileMenuOpen) return;

    const background = [
      document.getElementById('main-content'),
      document.querySelector('footer'),
      document.querySelector('a[href="#main-content"]'),
    ].filter((element): element is HTMLElement => Boolean(element));
    const previousInertState = background.map((element) => element.inert);

    background.forEach((element) => {
      element.inert = true;
    });

    return () => {
      background.forEach((element, index) => {
        element.inert = previousInertState[index];
      });
    };
  }, [isMobile, isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    await logout();
    toast({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
    setIsMobileMenuOpen(false);
  };


  const handleFavoriteClick = () => {
    if (!isAuthenticated) {
      toast({
        title: "Sign in required",
        description: "Please sign in to view your saved displays and sequences",
        variant: "destructive",
      });
      navigate(buildAuthHref('/auth', `${location.pathname}${location.search}${location.hash}`));
      return;
    }
    
    navigate('/favorites');
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        inert={isMobile && isMobileMenuOpen}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-300 ease-in-out",
          "bg-background/80 backdrop-blur-md shadow-xs",
          isMobile ? "px-4 py-3" : "px-6 py-4"
        )}
      >
        {/* Mobile Header */}
        {isMobile ? (
          <div className="flex items-center justify-between">
            {/* Hamburger Menu */}
            <Button
              variant="ghost"
              size="icon"
              ref={mobileMenuTriggerRef}
              className="h-11 w-11 rounded-full -ml-2"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation-menu"
            >
              {isMobileMenuOpen ? <X size={32} aria-hidden="true" /> : <MenuIcon size={32} aria-hidden="true" />}
            </Button>

            {/* Logo - centered */}
            <Link
              to="/"
              viewTransition
              className="text-xl font-bold flex items-center space-x-2 group absolute left-1/2 transform -translate-x-1/2"
            >
              <Logo />
            </Link>

            {/* Right side actions */}
            <div className="flex items-center space-x-2">
              {/* Enhanced Search */}
              <EnhancedSearchPopup open={searchOpen} onOpenChange={setSearchOpen}>
                <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full" aria-label="Search">
                  <Search size={20} aria-hidden="true" />
                </Button>
              </EnhancedSearchPopup>

              {/* Sign Up/Profile Button */}
              {isAuthenticated ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full"
                  onClick={() => navigate('/profile')}
                  aria-label="Open profile"
                >
                  <User size={20} aria-hidden="true" />
                </Button>
              ) : (
                <AuthDialog
                  triggerClassName="h-11 px-3 text-sm font-medium"
                  triggerText="Sign up"
                  initialView="signUp"
                />
              )}
            </div>
          </div>
        ) : (
          /* Desktop Header */
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link
              to="/"
              viewTransition
              className="text-2xl font-bold flex items-center space-x-2 group"
            >
              <Logo />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/displays" viewTransition className="text-foreground hover:text-primary font-medium transition-colors">
                Displays
              </Link>
              <Link to={buildDisplayMapPath()} viewTransition className="text-foreground hover:text-primary font-medium transition-colors">
                Map
              </Link>
              <div className="text-foreground hover:text-primary font-medium transition-colors">
                <SequencesMegaMenu />
              </div>
            </nav>

            {/* Desktop Search and Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <EnhancedSearchPopup open={searchOpen} onOpenChange={setSearchOpen}>
                <Button variant="outline" size="icon" className="rounded-full" aria-label="Search">
                  <Search size={18} aria-hidden="true" />
                </Button>
              </EnhancedSearchPopup>

              {/* Create button: "Upload sequence" on sequence pages, "Add a display" everywhere else */}
              {isAuthenticated && showCreateAction && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full px-3"
                  onClick={() => navigate(createAction.to)}
                >
                  <createAction.Icon size={16} className="mr-1" aria-hidden="true" />
                  {createAction.label}
                </Button>
              )}
              
              {/* Admin Button for admin user */}
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full px-3"
                  onClick={() => navigate('/admin')}
                >
                  <Settings size={16} className="mr-1" aria-hidden="true" />
                  Admin
                </Button>
              )}
              
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full"
                onClick={handleFavoriteClick}
              >
                <Heart size={18} aria-hidden="true" />
                <span className="sr-only">View Saved</span>
              </Button>
              {/* Realtime: Notification Bell */}
              <NotificationBell />
              <AuthPopover />
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu Overlay */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div
            className={cn(
              "fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity! duration-300! ease-out!",
              isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            aria-hidden="true"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Slide-out Menu */}
          <div 
            ref={mobileMenuRef}
            id="mobile-navigation-menu"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-navigation-title"
            aria-hidden={!isMobileMenuOpen}
            inert={!isMobileMenuOpen}
            tabIndex={-1}
            className={cn(
              "fixed left-0 top-0 h-full w-[280px] bg-background z-50 md:hidden transition-transform! duration-300! ease-drawer! shadow-xl",
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            <span id="mobile-navigation-title" className="sr-only">Navigation menu</span>
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <Logo />
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <X size={20} aria-hidden="true" />
              </Button>
            </div>

            {/* Menu Content */}
            <div className="flex flex-col h-[calc(100%-73px)]">
              <nav className="flex-1 overflow-y-auto">
                {/* Main Navigation */}
                <div className="p-4 space-y-1">
                  <Link
                    to="/"
                    viewTransition
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Home size={20} aria-hidden="true" />
                    <span className="font-medium">Home</span>
                  </Link>
                  <Link
                    to="/displays"
                    viewTransition
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MapPin size={20} aria-hidden="true" />
                    <span className="font-medium">Displays</span>
                  </Link>
                  <Link
                    to={buildDisplayMapPath()}
                    viewTransition
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MapIcon size={20} aria-hidden="true" />
                    <span className="font-medium">Map</span>
                  </Link>
                  <Link
                    to="/sequences"
                    viewTransition
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Sparkles size={20} aria-hidden="true" />
                    <span className="font-medium">Sequences</span>
                  </Link>
                </div>

                <div className="border-t">
                  <div className="p-4 space-y-1">
                      <Link
                        to="/bounties"
                        viewTransition
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Target size={20} aria-hidden="true" />
                        <span className="font-medium">Bounties</span>
                      </Link>
                      <Link
                        to="/videos"
                        viewTransition
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Video size={20} aria-hidden="true" />
                        <span className="font-medium">Videos</span>
                      </Link>
                    <button
                      className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors w-full text-left"
                      onClick={handleFavoriteClick}
                    >
                      <Heart size={20} aria-hidden="true" />
                      <span className="font-medium">Saved</span>
                    </button>
                  </div>
                </div>

                {/* User Section */}
                {isAuthenticated && (
                  <div className="border-t">
                    <div className="p-4 space-y-1">
                      <Link
                        to="/profile"
                        viewTransition
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <User size={20} aria-hidden="true" />
                        <span className="font-medium">Profile settings</span>
                      </Link>
                      <Link
                        to="/messages"
                        viewTransition
                        className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <MessageCircle size={20} aria-hidden="true" />
                        <span className="font-medium">Messages</span>
                      </Link>
                      {showCreateAction && (
                        <Link
                          to={createAction.to}
                          viewTransition
                          className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <createAction.Icon size={20} aria-hidden="true" />
                          <span className="font-medium">{createAction.label}</span>
                        </Link>
                      )}
                      {!hideSelling && (
                        <Link
                          to="/seller-dashboard"
                          viewTransition
                          className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Store size={20} aria-hidden="true" />
                          <span className="font-medium">Sales &amp; payouts</span>
                        </Link>
                      )}
                      {isAdmin && (
                        <Link
                          to="/admin"
                          viewTransition
                          className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Settings size={20} aria-hidden="true" />
                          <span className="font-medium">Admin</span>
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </nav>

              {/* Bottom Actions */}
              <div className="p-4 border-t">
                {isAuthenticated ? (
                  <Button 
                    variant="default" 
                    className="w-full"
                    onClick={handleSignOut}
                  >
                    <LogOut size={18} className="mr-2" aria-hidden="true" /> Sign out
                  </Button>
                ) : (
                  <AuthDialog triggerClassName="w-full" triggerText="Sign up" initialView="signUp" />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NavBar;
